import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiSearch, FiBriefcase, FiZap, FiList, FiTrendingUp, FiEdit2, FiCheck, FiX, FiUsers, FiBarChart2, FiEye, FiTrash2, FiCopy, FiExternalLink } from 'react-icons/fi';
import "../index.css";
import "./EmployeeDashboard.css";
import "./EmployeeLeaves.css";

const API_BASE_URL = 'https://ingrainhirebackend.ingrainsystems.com/api';

const ROLE_MAPPING = {
  "Doctor": ["Consultant", "Medical Officer", "Physician"],
  "MERN Stack Developer": ["Web Developer", "Software Engineer", "Frontend Developer", "Backend Developer"],
  "Product Specialist": ["Marketing Executive", "Sales", "Business Development"],
  "Staff Nurse": ["Nursing Staff", "Nurse"],
};

function JobPositionsStatus() {
  const [jobs, setJobs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingJobId, setEditingJobId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState("All");
  const [selectedRole, setSelectedRole] = useState("All");
  
  const navigate = useNavigate();

  // Get clientId from localStorage
  const getClientId = () => {
    return localStorage.getItem('clientId') || localStorage.getItem('clientCustomId') || '';
  };

  useEffect(() => {
    const clientId = getClientId();
    if (clientId) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchData = async () => {
    const clientId = getClientId();
    if (!clientId) {
      console.error("No clientId found");
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      // Fetch Jobs - response mein jobPosts array hai
      const jobsRes = await axios.get(`${API_BASE_URL}/jobs/all/${clientId}`);
      console.log("Jobs Response:", jobsRes.data);
      
      if (jobsRes.data.success) {
        // Response mein jobPosts array hai
        const jobsData = jobsRes.data.jobPosts || [];
        setJobs(jobsData);
        console.log("Jobs set:", jobsData.length);
      } else {
        setJobs([]);
      }
      
      // Fetch Employees
      try {
        const empsRes = await axios.get(`${API_BASE_URL}/employees/get-employees/${clientId}`);
        console.log("Employees Response:", empsRes.data);
        
        if (empsRes.data.success || Array.isArray(empsRes.data)) {
          const empData = Array.isArray(empsRes.data) ? empsRes.data : empsRes.data.employees || empsRes.data.data || [];
          setEmployees(empData);
          console.log("Employees set:", empData.length);
        } else {
          setEmployees([]);
        }
      } catch (empErr) {
        console.error("Failed to fetch employees:", empErr);
        setEmployees([]);
      }
      
    } catch (err) {
      console.error("Failed to fetch data:", err);
      setJobs([]);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeCount = (dept, role) => {
    if (!role) return 0;
    
    const roleAliases = [role.toLowerCase().trim(), ...(ROLE_MAPPING[role] || []).map(r => r.toLowerCase())];
    
    return employees.filter(emp => {
        const empDept = (emp.department || emp.dept || "").toLowerCase().trim();
        const empRole = (emp.role || emp.position || emp.designation || "").toLowerCase().trim();
        const empStatus = (emp.status || "").toLowerCase().trim();
        
        // Match department if specified
        const deptMatch = (dept && dept.toLowerCase() !== 'general' && dept.toLowerCase() !== 'all') 
            ? empDept.includes(dept.toLowerCase().trim()) 
            : true;

        // Match role or any of its aliases
        const roleMatch = roleAliases.some(alias => empRole.includes(alias));
        
        return deptMatch && roleMatch && empStatus === "active";
    }).length;
  };

  const startEditing = (job, field) => {
    setEditingJobId(job._id);
    setEditingField(field);
    setEditValue(job[field] === 'General' ? '' : (job[field] || ''));
  };

  const cancelEditing = () => {
    setEditingJobId(null);
    setEditingField(null);
    setEditValue("");
  };

  const saveEdit = async (jobId) => {
    const clientId = getClientId();
    if (!clientId) return;
    
    setSaving(true);
    try {
      const updateData = { 
        [editingField]: editingField === 'vacancies' ? Number(editValue) : editValue,
        clientId: clientId
      };
      const res = await axios.put(`${API_BASE_URL}/jobs/${jobId}`, updateData);
      if (res.data.success) {
        setJobs(prev => prev.map(j => j._id === jobId ? { ...j, ...updateData } : j));
        setEditingJobId(null);
        setEditingField(null);
      } else {
        alert("Update failed: " + (res.data.message || "Unknown error"));
      }
    } catch (err) {
      console.error("Failed to update:", err);
      alert("Update failed: " + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const clientId = getClientId();
  if (!clientId) {
    return (
      <div className="w-full min-h-screen p-4 lg:p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-indigo-50 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Please login to access job positions</p>
        </div>
      </div>
    );
  }

  // Get unique departments from jobs
  const getUniqueDepartments = () => {
    const depts = jobs.map(job => (job.department || 'General').trim());
    return ["All", ...new Set(depts)].sort();
  };

  // Get unique roles from jobs
  const getUniqueRoles = () => {
    const roles = jobs.map(job => job.role).filter(Boolean);
    return ["All", ...new Set(roles)].sort();
  };

  // Filter jobs based on selection
  const getDisplayedJobs = () => {
    let filtered = [...jobs];
    
    // Filter by department
    if (selectedDepartment !== "All") {
      filtered = filtered.filter(job => {
        const jobDept = (job.department || "General").trim();
        return jobDept === selectedDepartment;
      });
    }
    
    // Filter by role
    if (selectedRole !== "All") {
      filtered = filtered.filter(job => job.role === selectedRole);
    }
    
    return filtered;
  };

  const displayedJobs = getDisplayedJobs();

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin mb-4" />
        <p className="text-gray-400 text-sm animate-pulse">Synchronizing records...</p>
      </div>
    );
  }

  return (
    <div style={{backgroundColor:'white'}} className="emp-dash ">
      <main className="p-4 sm:p-6 lg:p-8">
        <div className="emp-dash__header">
          <div>
            <h1 className="emp-dash__greeting">
              Positions <span>Status</span>
            </h1>
            <p className="emp-dash__subtitle">
              Monitor active roles, vacancies, and fulfillment rates.
            </p>
          </div>
          <div className="emp-dash__date-pill">
            <FiBarChart2 />
            <span>
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>
        </div>

      {/* Filters Section */}
      <div className="emp-dash__card mb-6">
        <div className="emp-dash__card-header">
          <div>
            <h3 className="emp-dash__card-title flex items-center gap-2">
              <FiSearch className="text-blue-600" /> Filter Positions
            </h3>
            <p className="emp-dash__card-desc">Filter by department or role</p>
          </div>
        </div>
        <div className="emp-dash__card-body bg-gray-50/50">
          <div className="flex flex-wrap items-center gap-2">
          
          {/* Department Filter */}
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Dept:</span>
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="pl-12 pr-8 py-1.5 text-xs font-semibold text-gray-700 border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none cursor-pointer appearance-none bg-white min-w-[140px]"
            >
              {getUniqueDepartments().map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
          
          {/* Role Filter */}
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Role:</span>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="pl-12 pr-8 py-1.5 text-xs font-semibold text-gray-700 border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none cursor-pointer appearance-none bg-white min-w-[140px]"
            >
              {getUniqueRoles().map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>

          {/* Post New Job Button */}
          <button
            onClick={() => navigate('/post-new-job')}
            className="h-8 px-3 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition flex items-center gap-1 ml-auto"
          >
            <FiPlus className="text-xs" />
            <span>Post New Job</span>
          </button>

          {/* Clear Filters Button */}
          {(selectedDepartment !== "All" || selectedRole !== "All") && (
            <button
              onClick={() => { setSelectedDepartment("All"); setSelectedRole("All"); }}
              className="h-8 px-3 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition"
            >
              Clear
            </button>
          )}
        </div>
      </div>
      </div>

      {jobs.length === 0 ? (
        <div className="bg-white rounded-3xl border border-dashed border-gray-200 p-20 flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-4">
            <FiBriefcase className="text-gray-300" size={32} />
          </div>
          <h3 className="text-lg font-bold text-gray-800">No Job Positions Yet</h3>
          <p className="text-gray-500 text-sm max-w-xs mt-2">Start by posting your first job opening to see statistics here.</p>
          <button
            onClick={() => navigate('/post-new-job')}
            className="mt-6 text-indigo-600 font-bold text-sm hover:underline"
          >
            Create first post
          </button>
        </div>
      ) : displayedJobs.length === 0 ? (
        <div className="bg-white rounded-3xl border border-dashed border-gray-200 p-20 flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-4">
            <FiSearch className="text-gray-300" size={32} />
          </div>
          <h3 className="text-lg font-bold text-gray-800">No matching positions</h3>
          <p className="text-gray-500 text-sm max-w-xs mt-2">Try changing your filters to see more results.</p>
        </div>
      ) : (
        <>
          <div className="emp-dash__card mb-6">
            <div className="emp-dash__table-wrap">
              <table className="emp-dash__table">
                <thead>
                  <tr>
                    <th className="py-2 text-center font-bold text-xs uppercase tracking-wider">Role</th>
                    <th className="py-2 text-center font-bold text-xs uppercase tracking-wider">Hired</th>
                    <th className="py-2 text-center font-bold text-xs uppercase tracking-wider">Salary</th>
                    <th className="py-2 text-center font-bold text-xs uppercase tracking-wider">Skills</th>
                    <th className="py-2 text-center font-bold text-xs uppercase tracking-wider">Vacancies</th>
                    <th className="py-2 text-center font-bold text-xs uppercase tracking-wider">Status</th>
                    <th className="py-2 text-center font-bold text-xs uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {displayedJobs.map((job) => {
                    const empCount = getEmployeeCount(job.department, job.role);
                    const isEditingDept = editingJobId === job._id && editingField === 'department';
                    const isEditingTotal = editingJobId === job._id && editingField === 'vacancies';

                    return (
                      <tr key={job._id} className="border-b hover:bg-gray-50 transition-colors">
                        <td className="px-2 py-2 text-center">
                          <div className="flex flex-col items-center">
                            <div className="text-sm font-bold text-gray-900">{job.role}</div>
                            <div className="flex items-center gap-1 mt-1">
                              <span className="text-[9px] text-gray-400">{job.location || 'N/A'}</span>
                              <span className="text-gray-300">•</span>
                              {isEditingDept ? (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="text"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    className="w-20 px-1 py-0.5 text-[9px] border border-indigo-300 rounded focus:ring-2 focus:ring-indigo-100 outline-none"
                                    autoFocus
                                    placeholder="Dept..."
                                  />
                                  <button onClick={() => saveEdit(job._id)} disabled={saving} className="text-emerald-600"><FiCheck size={10} /></button>
                                  <button onClick={cancelEditing} className="text-rose-600"><FiX size={10} /></button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 group/edit-dept">
                                  <span className={`text-[9px] font-semibold transition-colors ${!job.department || job.department === 'General' ? 'text-gray-400 italic' : 'text-indigo-600'}`}>
                                    {job.department || 'General'}
                                  </span>
                                  <button 
                                    onClick={() => startEditing(job, 'department')}
                                    className="text-gray-300 hover:text-indigo-600 opacity-0 group-hover/edit-dept:opacity-100 transition-all"
                                  >
                                    <FiEdit2 size={8} />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <FiUsers className="text-gray-400" size={12} />
                            <span className="text-sm font-bold text-gray-600">{empCount}</span>
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <span className="text-sm font-medium text-emerald-600">{job.salary || '-'}</span>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <div className="flex flex-wrap justify-center gap-1 max-w-[150px] mx-auto">
                            {job.skills ? job.skills.split(',').map((skill, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-[9px] font-medium truncate max-w-[60px]">
                                {skill.trim()}
                              </span>
                            )) : <span className="text-gray-400 text-[9px]">-</span>}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <FiBriefcase className="text-gray-300" size={12} />
                            {isEditingTotal ? (
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="w-12 px-1 py-0.5 text-sm border border-indigo-300 rounded focus:ring-2 focus:ring-indigo-100 outline-none"
                                  min="0"
                                  autoFocus
                                />
                                <button onClick={() => saveEdit(job._id)} disabled={saving} className="text-emerald-600"><FiCheck size={10} /></button>
                                <button onClick={cancelEditing} className="text-rose-600"><FiX size={10} /></button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 group/edit-total">
                                <span className="text-sm font-medium text-gray-600">{job.vacancies ?? 0}</span>
                                <button 
                                  onClick={() => startEditing(job, 'vacancies')}
                                  className="text-gray-300 hover:text-indigo-600 opacity-0 group-hover/edit-total:opacity-100 transition-all"
                                >
                                  <FiEdit2 size={10} />
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${
                            job.status === 'active' 
                              ? 'bg-emerald-50 text-emerald-600' 
                              : job.status === 'draft'
                              ? 'bg-amber-50 text-amber-600'
                              : 'bg-gray-100 text-gray-500'
                          }`}>
                            {job.status || 'active'}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <div className="flex justify-center gap-2">
                            <button 
                              onClick={() => navigate(`/post-new-job?role=${encodeURIComponent(job.role)}&department=${encodeURIComponent(job.department || 'General')}`)}
                              className="text-blue-500 hover:text-blue-700"
                              title="View/Edit Job"
                            >
                              <FiEye size={14} />
                            </button>
                            <button 
                              onClick={() => navigate(`/post-new-job?role=${encodeURIComponent(job.role)}&department=${encodeURIComponent(job.department || 'General')}`)}
                              className="text-yellow-500 hover:text-yellow-700"
                              title="Edit Job"
                            >
                              <FiEdit2 size={14} />
                            </button>
                            <button 
                              onClick={() => navigate(`/post-new-job?role=${encodeURIComponent(job.role)}&department=${encodeURIComponent(job.department || 'General')}`)}
                              className="text-gray-400 hover:text-indigo-600"
                              title="Post Similar Job"
                            >
                              <FiPlus size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Stats Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="emp-dash__card p-6">
                  <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                          <FiUsers size={20} />
                      </div>
                      <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Total Active Workforce</p>
                          <h4 className="text-xl font-bold text-gray-900">
                              {employees.length}
                          </h4>
                      </div>
                  </div>
              </div>
              <div className="emp-dash__card p-6">
                  <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
                          <FiZap size={20} />
                      </div>
                      <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Total Planned Positions</p>
                          <h4 className="text-xl font-bold text-gray-900">
                              {displayedJobs.reduce((acc, job) => acc + (job.vacancies ?? 1), 0)}
                          </h4>
                      </div>
                  </div>
              </div>

              <div className="emp-dash__card p-6">
                  <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
                          <FiTrendingUp size={20} />
                      </div>
                      <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Fulfillment Rate</p>
                          <h4 className="text-xl font-bold text-gray-900">
                              {(() => {
                                  const totalPositions = displayedJobs.reduce((acc, job) => acc + (job.vacancies ?? 1), 0);
                                  if (totalPositions === 0) return "0%";
                                  const totalActiveMatched = displayedJobs.reduce((acc, job) => acc + getEmployeeCount(job.department, job.role), 0);
                                  return `${Math.round((totalActiveMatched / totalPositions) * 100)}%`;
                              })()}
                          </h4>
                      </div>
                  </div>
              </div>
          </div>
        </>
      )}
      </main>
    </div>
  );
}

export default JobPositionsStatus;